import { inject, Injectable } from '@angular/core';
import { FeatureFlagsService } from '@coin/shared/core/data-access';
import { PermissionAction, PermissionResource } from '@coin/shared/core/util-enums';
import {
  ActiveRoute,
  findNavigationRoot,
  findTitledRootChild,
  getNavigationChildren,
  joinRoutePath,
  resolveRouteTitle
} from '@coin/shared/core/util-helpers';
import { CoinRouteData, CoinTab, NavigationLink, RBAF_PERMISSION_RESOURCES } from '@coin/shared/core/util-models';
import { UserState } from './user.state';
import { map } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly userState = inject(UserState);

  readonly isHead$ = this.userState.currentUserDetails$.pipe(map(details => details?.isHead));
  readonly isGlobalHrBp$ = this.userState.currentUserDetails$.pipe(map(details => details?.isGlobalHrBp));

  public isSuperAdmin(): boolean {
    return this.hasPermissions(PermissionResource.All);
  }

  public isSuperAdminBasedOnRbaf(): boolean {
    return this.hasViewAccess(PermissionResource.All);
  }

  public isOrgPlanningAdmin(): boolean {
    return this.hasPermissions(PermissionResource.OrganisationPlanningAdmin);
  }

  public isHeadcountApprovalAdmin(): boolean {
    return this.hasPermissions(PermissionResource.HeadcountApprovalAdmin);
  }

  public isHeadcountController(): boolean {
    return this.hasPermissions(PermissionResource.HeadcountController);
  }

  public isStandingPeHr(): boolean {
    return (
      this.hasPermissions(PermissionResource.StandingPositionEvaluationHR) && !this.isSuperAdmin() && !this.isCAndB()
    );
  }

  /*
   @returns `true` if the user has any permission AND any action on that permission, otherwise `false`
  public hasPermissionsAndActions({ resources, actions }: { resources: PermissionResource[]; actions: PermissionAction[] }): boolean {
    if (this.isSuperAdmin()) return true;
    if (this.hasViewAccess(...resources)) return true; // RBAF view access counts as all actions

    const userPermissions = this.userState.allPermissions();
    return resources.some(resource => {
      const userResource = userPermissions.get(resource);
      if (!userResource) return false;
      if (userResource.httpActions.has(PermissionAction.All)) return true;
      if (!actions?.length) return true;
      return actions.some(action => userResource.httpActions.has(action));
    });
  }
  */

  // Intermediate solution for permission split currently in place
  /** @returns `true` if the user has any permission AND any action on that permission, otherwise `false` */
  public hasPermissionsAndActions({
    resources,
    actions
  }: {
    resources: PermissionResource[];
    actions: PermissionAction[];
  }): boolean {
    if (this.isSuperAdmin() || this.isSuperAdminBasedOnRbaf()) return true;

    const rbafResources = new Set(RBAF_PERMISSION_RESOURCES);
    const meritResources = resources.filter(res => rbafResources.has(res));
    const otherResources = resources.filter(res => !rbafResources.has(res) || res === PermissionResource.Emulation);
    const hasMeritResources = meritResources.length > 0;
    const hasOtherResources = otherResources.length > 0;

    if (!hasMeritResources && !hasOtherResources) {
      return false;
    }

    const userPermissions = this.userState.allPermissions();
    const hasPermissionResourceActions = otherResources.some(resource => {
      const userResource = userPermissions.get(resource);
      if (!userResource) return false;
      if (userResource.httpActions.has(PermissionAction.All)) return true;
      if (!actions?.length) return true;
      return actions.some(action => userResource.httpActions.has(action));
    });

    const hasMeritPermissions = hasMeritResources ? this.hasViewAccess(...meritResources) : false; // RBAF view access counts as all actions
    const hasOtherPermissions = hasOtherResources
      ? hasPermissionResourceActions || this.hasViewAccess(...otherResources)
      : false;

    return hasMeritPermissions || hasOtherPermissions;
  }

  /*
  /!** @returns `true` if the user has any permission, otherwise `false` *!/
  public hasPermissions(...resources: PermissionResource[]): boolean {
    return this.hasPermissionResource(...resources) || this.hasViewAccess(...resources);
  }
  */

  // Intermediate solution for permission split currently in place
  /** @returns `true` if the user has any permission, otherwise `false`.
   * Merit resources are handled by RBAF endpoint groups, other resources are still handled on role permissions
   * */
  public hasPermissions(...resources: PermissionResource[]): boolean {
    const rbafResources = new Set(RBAF_PERMISSION_RESOURCES);
    const meritResources = resources.filter(res => rbafResources.has(res));
    const otherResources = resources.filter(res => !rbafResources.has(res) || res === PermissionResource.Emulation);
    const hasMeritResources = meritResources.length > 0;
    const hasOtherResources = otherResources.length > 0;

    if (!hasMeritResources && !hasOtherResources) {
      return false;
    }

    const hasMeritPermissions = hasMeritResources ? this.hasViewAccess(...meritResources) : false;
    const hasOtherPermissions = hasOtherResources
      ? this.hasPermissionResource(...otherResources) || this.hasViewAccess(...otherResources)
      : false;
    return hasMeritPermissions || hasOtherPermissions;
  }

  // permissions based on role permission resources
  private hasPermissionResource(...resources: PermissionResource[]): boolean {
    const userPermissions = this.userState.allPermissions();
    return userPermissions?.has(PermissionResource.All) || resources.some(resource => userPermissions?.has(resource));
  }

  // permission based on endpoint groups (RBAF)
  private hasViewAccess(...resources: PermissionResource[]): boolean {
    const accessViews = this.userState.accessViews();
    return accessViews?.has(PermissionResource.All) || resources.some(accessView => accessViews?.has(accessView));
  }

  public isCAndB(): boolean {
    return this.hasPermissions(PermissionResource.CAndB);
  }

  public isHeadcountApprovalHr(): boolean {
    return this.hasPermissions(PermissionResource.HeadcountApprovalHr);
  }

  public isHeadcountApprovalUser(): boolean {
    return this.hasPermissions(PermissionResource.HeadcountApprovalUser);
  }

  // region Navigation
  getNavigationLinks(url: URL, chain: ActiveRoute[]): NavigationLink[] {
    const navigationRoot = findNavigationRoot(chain);
    if (!navigationRoot) {
      console.error(`No navigation-root found for ${url.pathname}!`);
      return [];
    }

    return getNavigationChildren(navigationRoot.config, chain)
      .filter(route => route.redirectTo === undefined && route.path !== '**')
      .map(route => ({ route, rootChild: findTitledRootChild(route, chain) }))
      .filter(({ route, rootChild }) => route.data?.title || rootChild)
      .filter(({ route }) => this.isRoutePermitted(route.data) && !route.data?.hiddenNavigation)
      .filter(({ route }) => this.isVisible(route.data, url))
      .map(({ route, rootChild }) => ({
        title: resolveRouteTitle(route.data?.title, url) ?? resolveRouteTitle(rootChild?.data.title, url),
        hasChilds: route.data?.hasChildren ?? !!route.children?.some(child => child.path !== ''),
        disabledTooltip: route.data?.disabledTooltip,
        url: joinRoutePath(navigationRoot.url, route.path),
        preserveParams: route.data?.params,
        icon: route.data?.icon,
        permissions: route.data?.permissions,
        featureFlag: route.data?.featureFlag
      }));
  }

  private isRoutePermitted(data: CoinRouteData | undefined): boolean {
    if (data?.featureFlag && this.featureFlagsService.isInactive(data.featureFlag)) {
      return false;
    }

    if (data?.permissions?.length > 0 && !this.hasPermissions(...data.permissions)) {
      return false;
    }

    return true;
  }

  private isVisible(data: CoinRouteData | undefined, url: URL): boolean {
    return (
      !data?.isVisible ||
      Object.entries(data.isVisible).every(([param, validValues]) => validValues.includes(url.searchParams.get(param)))
    );
  }

  public getSubPageTabs(url: URL, chain: ActiveRoute[]): CoinTab[] {
    const navigationRoot = findNavigationRoot(chain);
    if (!navigationRoot) {
      console.error(`No navigation-root found for ${url.pathname}!`);
      return [];
    }
    if (navigationRoot.config.data?.hideTabs) {
      return [];
    }

    const parentRoute = navigationRoot.url.slice(1);
    const disabledTabTooltip = 'general.disabled-nav-tab';
    return getNavigationChildren(navigationRoot.config, chain)
      .filter(route => route.redirectTo === undefined && route.path !== '**')
      .filter(route => url.pathname.slice(1).startsWith(joinRoutePath(parentRoute, route.path)))
      .filter(route => this.isRoutePermitted(route.data))
      .flatMap(route => {
        const childs = getNavigationChildren(route, chain).filter(
          child => child.redirectTo === undefined && child.path !== '**' && !!child.data?.title
        );
        const hasEmptyRoute = childs.some(child => child.path === '');
        if (!childs.length) {
          return [];
        }

        const baseRoute = joinRoutePath(parentRoute, route.path);
        let tabs = childs
          .filter(child => this.isRoutePermitted(child.data))
          .filter(child => this.isVisible(child.data, url))
          .map(
            child =>
              ({
                name: child.data.title,
                isDisabled: !!child.data.paramTitle && !url.searchParams.has(child.data.paramTitle),
                isSelected: url.pathname === joinRoutePath(baseRoute, child.path),
                route: joinRoutePath(baseRoute, child.path),
                tooltip: disabledTabTooltip
              }) as CoinTab
          );

        if (!hasEmptyRoute && !route.data?.emptyPage) {
          tabs = [
            {
              name: resolveRouteTitle(route.data?.title, url),
              isDisabled: false,
              isSelected: url.pathname.endsWith(route.path),
              route: baseRoute,
              tooltip: disabledTabTooltip
            },
            ...tabs
          ];
        }

        return tabs.length === 1 ? [] : tabs;
      });
  }

  // endregion
}
